import { format } from 'date-fns';

import { makeYmdList } from '../domain/ki/date';

/**
 * 期間スライドの結果が、機種集計と同じ比較期間ルールで再計算する対象か
 * （今週・今月だけ。当日・前日・前週・前月・過去N日・カスタムは各画面の既存計算のまま）
 */
export const isKiAlignedSlideDateRange = (
  dateRange: string
): dateRange is '今週' | '今月' => dateRange === '今週' || dateRange === '今月';

const toYmdList = (dates: Date[]) =>
  dates.map((date) => format(date, 'yyyy-MM-dd'));

/**
 * 今週・今月のスライド結果を、機種集計の makeYmdList（比較区分は今週→前週、今月→前月）で確定する
 */
export const makeKiAlignedSlideDates = (
  dateRange: '今週' | '今月',
  options: { excludeToday: boolean; currentDate?: Date }
) => {
  const { ymdList, ymdComparisonList } = makeYmdList(
    dateRange,
    dateRange === '今週' ? '前週' : '前月',
    {
      currentDate: options.currentDate,
      excludeToday: options.excludeToday,
    }
  );

  const ymdListAsString = toYmdList(ymdList);
  const ymdComparisonListAsString = toYmdList(ymdComparisonList);
  const startDate = ymdListAsString.at(0);
  const endDate = ymdListAsString.at(-1);
  const startComparisonDate = ymdComparisonListAsString.at(0);
  const endComparisonDate = ymdComparisonListAsString.at(-1);

  if (
    startDate == null ||
    endDate == null ||
    startComparisonDate == null ||
    endComparisonDate == null
  ) {
    throw new Error('機種集計と同じ期間計算の結果が空です');
  }

  return {
    ymdList: ymdListAsString,
    ymdComparisonList: ymdComparisonListAsString,
    startDate,
    endDate,
    startComparisonDate,
    endComparisonDate,
  };
};

/**
 * 台別のスライドで確定した今週・今月。
 * お気に入りには保存せず、期間を変えるまでの再検索だけに使う。
 */
export type KiAlignedYmdSlideLock = {
  dateRange: '今週' | '今月';
  dateUnit: string;
  isComparison: boolean;
  excludeToday: boolean;
  ymdList: string[];
  ymdComparisonList: string[];
};

/**
 * PPMのスライドで確定した今週・今月。
 * お気に入りには保存せず、期間を変えるまでの再検索だけに使う。
 */
export type KiAlignedStartEndSlideLock = {
  dateRange: '今週' | '今月';
  isComparison: boolean;
  excludeToday: boolean;
  startDate: string;
  endDate: string;
  startComparisonDate: string;
  endComparisonDate: string;
};

export const isSameYmdList = (
  left: readonly string[] | undefined,
  right: readonly string[] | undefined
): boolean => {
  if (left == null || right == null) {
    return left == null && right == null;
  }
  if (left.length !== right.length) {
    return false;
  }
  return left.every((ymd, index) => ymd === right.at(index));
};

const isSameSlidePeriod = (
  lock: {
    dateRange: '今週' | '今月';
    isComparison: boolean;
    excludeToday: boolean;
    dateUnit?: string;
  },
  next: {
    dateRange: string;
    isComparison: boolean;
    excludeToday: boolean;
    dateUnit?: string;
  }
) =>
  lock.dateRange === next.dateRange &&
  lock.isComparison === next.isComparison &&
  lock.excludeToday === next.excludeToday &&
  (lock.dateUnit === undefined || lock.dateUnit === next.dateUnit);

/**
 * フォーム再検索で、スライド確定の日付を維持するか。
 * スライド操作そのものは true。期間・日付単位・比較有無・当日除外・日付が同じときも維持する。
 */
export const shouldKeepKiAlignedYmdSlide = (
  shouldUseSlideDates: boolean,
  lock: KiAlignedYmdSlideLock | undefined,
  next: {
    dateRange: string;
    dateUnit: string;
    isComparison: boolean;
    excludeToday: boolean;
    ymdList: readonly string[] | undefined;
    ymdComparisonList: readonly string[] | undefined;
  }
): boolean => {
  if (shouldUseSlideDates) {
    return true;
  }
  if (lock == null || !isSameSlidePeriod(lock, next)) {
    return false;
  }
  if (!isSameYmdList(lock.ymdList, next.ymdList)) {
    return false;
  }
  if (
    next.isComparison &&
    !isSameYmdList(lock.ymdComparisonList, next.ymdComparisonList)
  ) {
    return false;
  }
  return true;
};

export const shouldKeepKiAlignedStartEndSlide = (
  shouldUseSlideDates: boolean,
  lock: KiAlignedStartEndSlideLock | undefined,
  next: {
    dateRange: string;
    isComparison: boolean;
    excludeToday: boolean;
    startDate: string | undefined;
    endDate: string | undefined;
    startComparisonDate: string | undefined;
    endComparisonDate: string | undefined;
  }
): boolean => {
  if (shouldUseSlideDates) {
    return true;
  }
  if (lock == null || !isSameSlidePeriod(lock, next)) {
    return false;
  }
  if (lock.startDate !== next.startDate || lock.endDate !== next.endDate) {
    return false;
  }
  if (
    next.isComparison &&
    (lock.startComparisonDate !== next.startComparisonDate ||
      lock.endComparisonDate !== next.endComparisonDate)
  ) {
    return false;
  }
  return true;
};

/** フォーム表示を、スライド確定の日付のままにするか */
export const isKiAlignedYmdSlideLockActive = (
  lock: KiAlignedYmdSlideLock | undefined,
  current: {
    dateRange: string;
    dateUnit: string;
    isComparison: boolean;
    excludeToday: boolean;
  }
): lock is KiAlignedYmdSlideLock =>
  lock != null && isSameSlidePeriod(lock, current);

export const isKiAlignedStartEndSlideLockActive = (
  lock: KiAlignedStartEndSlideLock | undefined,
  current: {
    dateRange: string;
    isComparison: boolean;
    excludeToday: boolean;
  }
): lock is KiAlignedStartEndSlideLock =>
  lock != null && isSameSlidePeriod(lock, current);
